import { Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AUTH_REGISTER_ROUTE_SEGMENTS } from '@tix/auth/constants';
import { TixAuthService } from '@tix/auth/services';
import { TixAnalyticsService } from '@tix/shared/analytics';
import { UserActions, UserPartialState } from '@tix/auth/state';
import * as AuthActions from '@tix/auth/state/actions';
import * as AuthSelectors from '@tix/auth/state/selectors';
import { Observable, Subject } from 'rxjs';
import { map, take, filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tix-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: 'tix-login'
  }
})
export class TixLoginComponent implements OnInit, OnDestroy {
  readonly REGISTER_ROUTE = ['/', ...AUTH_REGISTER_ROUTE_SEGMENTS];

  loginAttempted = false;

  showPassword: boolean = false;

  error: Observable<string>;

  loginForm = new FormGroup({
    email: new FormControl(null, [Validators.required, Validators.email]),
    password: new FormControl(null, [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(256)
    ])
  });

  get passwordLength(): number {
    return this.loginForm.get('password')?.value?.length ?? 0;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<UserPartialState>,
    private readonly route: ActivatedRoute,
    private tixAuthService: TixAuthService,
    private router: Router,
    private analytics: TixAnalyticsService
  ) {
    this.error = this.store
      .select(AuthSelectors.getUserError)
      .pipe(map(res => (res ? res : '')));

    this.error
      .pipe(
        filter(err => !!err),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.loginAttempted) {
          this.analytics.captureEvent('login_invalid_credentials');
        }
      });
  }

  hasError(control: string, error: string): boolean {
    return this.loginForm.get(control)?.hasError(error) ?? false;
  }

  ngOnInit(): void {
    this.analytics.captureEvent('login_page_viewed');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  login(): void {
    if (!this.loginForm.valid) {
      this.analytics.captureEvent('login_submit', { valid: false });
      return;
    }
    this.loginAttempted = true;
    this.analytics.captureEvent('login_submit', {
      valid: true,
      method: 'password'
    });

    this.route.queryParams
      .pipe(
        take(1),
        map(params => params.r)
      )
      .subscribe(returnUrl =>
        this.store.dispatch(
          AuthActions.loginAction({
            credentials: this.loginForm.value,
            returnUrl: returnUrl ?? '/'
          })
        )
      );
    this.store.dispatch(UserActions.fetchUserRoles());
  }

  onCreateAccountClick(event: Event) {
    this.analytics.captureEvent('create_account_clicked');
  }

  onForgotPasswordClick(event: Event) {
    this.analytics.captureEvent('forgot_password_clicked');
  }

  authWith(media: 'google' | 'fb' | 'apple' | 'twitter') {
    this.store.dispatch(UserActions.loginWihMediaAction({ media }));
    if (media === 'google') {
      this.analytics.captureEvent('login_with_google_clicked');
    } else {
      this.analytics.captureEvent('login_with_social_clicked', {
        provider: media
      });
    }
  }
}
